// src/services/taskService.js
import { pb } from "./pocketbase";
import { notificationService } from "./notificationService";
import { chatService } from "./chatService";

export const taskService = {
  // --- 1. BUSCAR TAREFAS ---
  async getTasks({ userId, operatorId, teamId = null, teamIds = [], status = null }) {
    try {
      const allTeams = new Set([
        ...(teamId ? [teamId] : []),
        ...(Array.isArray(teamIds) ? teamIds : []),
      ]);

      if (!userId && !operatorId && allTeams.size === 0) {
        return [];
      }

      const userFilters = [];
      if (userId) {
        userFilters.push(`assigned_user = "${userId}"`);
        userFilters.push(`created_by_user = "${userId}"`);
      }
      if (operatorId) {
        userFilters.push(`assigned_operator = "${operatorId}"`);
      }
      allTeams.forEach((tid) => {
        userFilters.push(`module_key = "team_${tid}"`);
        userFilters.push(`assigned_team = "${tid}"`);
      });

      let queryFilter = `status != "cancelada" && (${userFilters.join(" || ")})`;
      if (status) {
        queryFilter += ` && status = "${status}"`;
      }

      let records = [];
      try {
        records = await pb.collection("system_tasks").getFullList({
          filter: queryFilter,
          sort: "-due_date,-created",
          expand: "assigned_user,assigned_operator,created_by_user,created_by_operator",
        });
      } catch (err) {
        // Fallback: se o campo assigned_team não existir como coluna no schema do PocketBase
        const safeFilters = [];
        if (userId) {
          safeFilters.push(`assigned_user = "${userId}"`);
          safeFilters.push(`created_by_user = "${userId}"`);
        }
        if (operatorId) {
          safeFilters.push(`assigned_operator = "${operatorId}"`);
        }
        allTeams.forEach((tid) => {
          safeFilters.push(`module_key = "team_${tid}"`);
        });

        let safeQuery = `status != "cancelada"`;
        if (safeFilters.length > 0) {
          safeQuery += ` && (${safeFilters.join(" || ")})`;
        }
        if (status) {
          safeQuery += ` && status = "${status}"`;
        }

        try {
          records = await pb.collection("system_tasks").getFullList({
            filter: safeQuery,
            sort: "-due_date,-created",
            expand: "assigned_user,assigned_operator,created_by_user,created_by_operator",
          });
        } catch (err2) {
          records = await pb.collection("system_tasks").getFullList({
            sort: "-due_date,-created",
            expand: "assigned_user,assigned_operator,created_by_user,created_by_operator",
          }).catch(() => []);
        }
      }

      // FILTRO ESTREITO E OBRIGATÓRIO EM MEMÓRIA (GARANTE 100% QUE NUNCA VAZA TAREFA DE TERCEIROS)
      const sanitized = (records || []).filter((t) => {
        if (t.status === "cancelada") return false;

        const isCreatedByMe = userId && t.created_by_user === userId;
        const isAssignedToMe = userId && t.assigned_user === userId;
        const isAssignedToMyOp = operatorId && t.assigned_operator === operatorId;
        const isAssignedToMyTeam =
          allTeams.size > 0 &&
          (allTeams.has(t.assigned_team) ||
            Array.from(allTeams).some((tid) => t.module_key === `team_${tid}`) ||
            Array.from(allTeams).some((tid) => t.description && t.description.includes(`[Equipe: ${tid}]`)));

        const belongsToMe = isCreatedByMe || isAssignedToMe || isAssignedToMyOp || isAssignedToMyTeam;
        if (!belongsToMe) return false;

        if (status && t.status !== status) return false;
        return true;
      });

      return sanitized;
    } catch (error) {
      console.warn("⚠️ [taskService] Erro ao buscar tarefas:", error);
      return [];
    }
  },

  // --- 2. CONTAR TAREFAS PENDENTES ---
  async getPendingCount({ userId, operatorId, teamId = null, teamIds = [] }) {
    try {
      const allTeams = new Set([
        ...(teamId ? [teamId] : []),
        ...(Array.isArray(teamIds) ? teamIds : []),
      ]);

      if (!userId && !operatorId && allTeams.size === 0) return 0;

      const userFilters = [];
      if (userId) userFilters.push(`assigned_user = "${userId}"`);
      if (operatorId) userFilters.push(`assigned_operator = "${operatorId}"`);
      allTeams.forEach((tid) => userFilters.push(`module_key = "team_${tid}"`));

      if (userFilters.length === 0) return 0;

      const filterQuery = `status != "concluida" && status != "cancelada" && (${userFilters.join(" || ")})`;

      try {
        const res = await pb.collection("system_tasks").getList(1, 1, {
          filter: filterQuery,
          fields: "id",
        });
        return res.totalItems || 0;
      } catch (e) {
        // Fallback: consulta tarefas e conta em memória
        const myTasks = await this.getTasks({ userId, operatorId, teamId, teamIds });
        return myTasks.filter((t) => t.status === "pendente" || t.status === "em_andamento").length;
      }
    } catch (e) {
      return 0;
    }
  },

  // --- 3. CRIAR NOVA TAREFA (INDIVIDUAL OU POR TIME) ---
  async createTask({
    title,
    description = "",
    assigned_user = null,
    assigned_operator = null,
    assigned_team = null,
    team_name = null,
    created_by_user,
    created_by_operator = null,
    order_number = null,
    module_key = null,
    priority = "media",
    due_date = null,
  }) {
    if (!title?.trim() || !created_by_user) {
      throw new Error("Título e criador da tarefa são obrigatórios.");
    }

    try {
      const finalModuleKey = assigned_team ? `team_${assigned_team}` : (module_key || null);

      const payload = {
        title: title.trim(),
        description: description ? description.trim() : "",
        priority,
        status: "pendente",
        order_number: order_number ? String(order_number).trim() : null,
        module_key: finalModuleKey,
        due_date: due_date || null,
        created_by_user: created_by_user.id,
      };

      if (assigned_user) payload.assigned_user = assigned_user;
      if (assigned_operator) payload.assigned_operator = assigned_operator;
      if (assigned_team) {
        payload.assigned_team = assigned_team;
        if (team_name) payload.team_name = team_name;
      }
      if (created_by_operator?.id) payload.created_by_operator = created_by_operator.id;

      let record = null;
      try {
        record = await pb.collection("system_tasks").create(payload);
      } catch (err) {
        // Fallback gracioso caso assigned_team ou team_name não existam como colunas em system_tasks
        if (payload.assigned_team || payload.team_name) {
          const safePayload = { ...payload };
          delete safePayload.assigned_team;
          delete safePayload.team_name;
          safePayload.description = `[Equipe: ${team_name || assigned_team}]\n${safePayload.description || ""}`.trim();
          record = await pb.collection("system_tasks").create(safePayload);
        } else {
          throw err;
        }
      }

      const creatorName = created_by_operator?.nome
        ? `${created_by_operator.nome} (Operador)`
        : created_by_user?.name || "Administrador";

      // --- CASO 1: ATRIBUIÇÃO DIRETA A USUÁRIO INDIVIDUAL ---
      if (assigned_user && assigned_user !== created_by_user.id) {
        notificationService.createNotification({
          recipient_user: assigned_user,
          type: "task_assigned",
          title: `Nova tarefa atribuída por ${creatorName}`,
          message: `[Prioridade: ${priority.toUpperCase()}] ${title}`,
          link_tab: "tasks",
          link_id: record.id,
        }).catch(() => {});

        // Envia também no chat pessoal (DM)
        try {
          const dmChannel = await chatService.getOrCreateDM({
            currentUser: created_by_user,
            currentOperator: created_by_operator,
            targetUser: { id: assigned_user },
          });
          if (dmChannel?.id) {
            const dueStr = due_date ? new Date(due_date).toLocaleDateString("pt-BR") : null;
            let chatText = `📋 **Nova Tarefa Atribuída**\n\n` +
              `**Título:** ${title.trim()}\n` +
              `**Prioridade:** ${priority.toUpperCase()}\n`;
            if (dueStr) chatText += `**Prazo:** 📅 ${dueStr}\n`;
            if (order_number) chatText += `**Pedido:** [${order_number}]\n`;
            if (description?.trim()) chatText += `\n**Instruções:**\n${description.trim()}`;

            await chatService.sendMessage({
              channelId: dmChannel.id,
              user: created_by_user,
              activeOperator: created_by_operator,
              content: chatText,
            });
          }
        } catch (e) {
          console.warn("Aviso ao enviar DM de tarefa individual:", e);
        }
      }

      // --- CASO 2: ATRIBUIÇÃO DIRETA A OPERADOR INDIVIDUAL ---
      if (assigned_operator && assigned_operator !== created_by_operator?.id) {
        notificationService.createNotification({
          recipient_operator: assigned_operator,
          type: "task_assigned",
          title: `Nova tarefa atribuída por ${creatorName}`,
          message: `[Prioridade: ${priority.toUpperCase()}] ${title}`,
          link_tab: "tasks",
          link_id: record.id,
        }).catch(() => {});

        try {
          const dmChannel = await chatService.getOrCreateDM({
            currentUser: created_by_user,
            currentOperator: created_by_operator,
            targetOperator: { id: assigned_operator },
          });
          if (dmChannel?.id) {
            const dueStr = due_date ? new Date(due_date).toLocaleDateString("pt-BR") : null;
            let chatText = `📋 **Nova Tarefa de Bancada**\n\n` +
              `**Título:** ${title.trim()}\n` +
              `**Prioridade:** ${priority.toUpperCase()}\n`;
            if (dueStr) chatText += `**Prazo:** 📅 ${dueStr}\n`;
            if (order_number) chatText += `**Pedido:** [${order_number}]\n`;
            if (description?.trim()) chatText += `\n**Instruções:**\n${description.trim()}`;

            await chatService.sendMessage({
              channelId: dmChannel.id,
              user: created_by_user,
              activeOperator: created_by_operator,
              content: chatText,
            });
          }
        } catch (e) {
          console.warn("Aviso ao enviar DM de tarefa para operador:", e);
        }
      }

      // --- CASO 3: ATRIBUIÇÃO A TIME / EQUIPE (RESOLUÇÃO DE MEMBROS E DMs) ---
      if (assigned_team) {
        try {
          // 1. Busca configurações de colaboradores associados a esta equipe
          const teamConfigs = await pb.collection("colaboradores_config").getFullList({
            filter: `equipe = "${assigned_team}"`,
            expand: "colaborador",
          }).catch(() => []);

          const memberUserIds = new Set();
          teamConfigs.forEach((c) => {
            if (c.colaborador) memberUserIds.add(c.colaborador);
          });

          // 2. Busca operadores associados aos usuários da equipe
          const activeOperators = await pb.collection("oficina_operadores").getFullList({
            filter: "ativo = true",
          }).catch(() => []);

          const targetMembers = [];

          memberUserIds.forEach((uId) => {
            if (uId !== created_by_user?.id) {
              targetMembers.push({ userId: uId });
            }
          });

          activeOperators.forEach((op) => {
            if (op.colaborador && memberUserIds.has(op.colaborador)) {
              targetMembers.push({ operatorId: op.id, opName: op.nome });
            }
          });

          const priorityEmoji =
            priority === "urgente" ? "🔴" : priority === "alta" ? "🟠" : priority === "media" ? "🟡" : "🟢";
          const teamLabel = team_name || "sua Equipe";
          const dueStr = due_date ? new Date(due_date).toLocaleDateString("pt-BR") : null;

          let chatMsgText =
            `📋 **Nova Tarefa Atribuída para ${teamLabel}**\n\n` +
            `**Título:** ${title.trim()}\n` +
            `**Prioridade:** ${priorityEmoji} ${priority.toUpperCase()}\n`;

          if (dueStr) chatMsgText += `**Prazo:** 📅 ${dueStr}\n`;
          if (order_number) chatMsgText += `**Pedido:** [${order_number}]\n`;
          if (description?.trim()) chatMsgText += `\n**Instruções:**\n${description.trim()}\n`;
          chatMsgText += `\n*(Tarefa compartilhada com a equipe. Ao concluir, marque na aba Tarefas)*`;

          // Dispara notificação e mensagem na DM para cada membro do time
          for (const member of targetMembers) {
            notificationService.createNotification({
              recipient_user: member.userId || null,
              recipient_operator: member.operatorId || null,
              type: "task_assigned",
              title: `Nova tarefa para ${teamLabel} atribuída por ${creatorName}`,
              message: `[Prioridade: ${priority.toUpperCase()}] ${title.trim()}`,
              link_tab: "tasks",
              link_id: record.id,
            }).catch(() => {});

            try {
              const dmChannel = await chatService.getOrCreateDM({
                currentUser: created_by_user,
                currentOperator: created_by_operator,
                targetUser: member.userId ? { id: member.userId } : null,
                targetOperator: member.operatorId ? { id: member.operatorId, nome: member.opName } : null,
              });

              if (dmChannel?.id) {
                await chatService.sendMessage({
                  channelId: dmChannel.id,
                  user: created_by_user,
                  activeOperator: created_by_operator,
                  content: chatMsgText,
                });
              }
            } catch (errDM) {
              console.warn("Erro ao enviar DM de tarefa para membro da equipe:", member, errDM);
            }
          }
        } catch (errTeam) {
          console.warn("Erro ao resolver membros do time para notificações:", errTeam);
        }
      }

      return record;
    } catch (error) {
      console.error("❌ [taskService] Erro ao criar tarefa:", error);
      throw error;
    }
  },

  // --- 4. ATUALIZAR STATUS DA TAREFA (COM CONCLUSÃO COLETIVA) ---
  async updateStatus(taskId, status, user = null) {
    if (!taskId) return null;

    try {
      const now = new Date().toISOString();
      const userName = user?.name || user?.username || "Colaborador";

      // Tentativa 1: Envia com todos os campos de auditoria
      const fullPayload = {
        status,
        completed_at: status === "concluida" ? now : null,
      };
      if (user?.id) {
        fullPayload.completed_by = user.id;
        fullPayload.completed_by_name = userName;
      }

      let record = null;
      try {
        record = await pb.collection("system_tasks").update(taskId, fullPayload);
        return record;
      } catch (err1) {
        // Tentativa 2: Sem completed_by e completed_by_name caso não existam como colunas
        try {
          record = await pb.collection("system_tasks").update(taskId, {
            status,
            completed_at: status === "concluida" ? now : null,
          });
          return {
            ...record,
            completed_by_name: status === "concluida" ? userName : null,
          };
        } catch (err2) {
          // Tentativa 3: Apenas o campo status (que é universal na tabela)
          record = await pb.collection("system_tasks").update(taskId, { status });
          return {
            ...record,
            status,
            completed_at: status === "concluida" ? now : null,
            completed_by_name: status === "concluida" ? userName : null,
          };
        }
      }
    } catch (error) {
      console.error("❌ [taskService] Erro ao atualizar status da tarefa:", error);
      throw error;
    }
  },

  // --- 5. EXCLUIR TAREFA ---
  async deleteTask(taskId) {
    if (!taskId) return false;
    try {
      // Tentativa 1: Exclusão física no PocketBase
      await pb.collection("system_tasks").delete(taskId);
      return true;
    } catch (error) {
      console.warn("⚠️ [taskService] Exclusão física falhou (provável regra de API), cancelando tarefa:", error);
      try {
        // Tentativa 2: Soft delete alterando o status para 'cancelada'
        await pb.collection("system_tasks").update(taskId, { status: "cancelada" });
        return true;
      } catch (cancelErr) {
        console.error("❌ [taskService] Falha ao excluir ou cancelar tarefa:", cancelErr);
        throw error;
      }
    }
  },
};
